import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

const DurationModal = ({ onSubmit, onClose }) => {
  const { t } = useTranslation();
  const [format, setFormat] = useState("simple"); // simple ou detaillé
  const [duration, setDuration] = useState("");
  const [detailedDuration, setDetailedDuration] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  // Gestion des formats de durée prédéfinis
  const presetDurations = [
    { label: "10min", value: "10min" },
    { label: "30min", value: "30min" },
    { label: "1h", value: "1h" },
    { label: "1h30", value: "1h30" },
    { label: "2h", value: "2h" },
    { label: t("artworkModals.duration.infiniteLoop"), value: t("artworkModals.duration.infiniteLoop") },
  ];

  const handleDurationChange = (e) => {
    setDuration(e.target.value);
  };

  const handleDetailedChange = (e) => {
    const { name, value } = e.target;
    const intValue = parseInt(value) || 0;
    
    // S'assurer que les valeurs sont dans des plages valides
    let validValue = intValue;
    if (name === "minutes" || name === "seconds") {
      validValue = Math.min(59, Math.max(0, intValue));
    } else {
      validValue = Math.max(0, intValue);
    }
    
    setDetailedDuration({
      ...detailedDuration,
      [name]: validValue
    });
  };

  const applyPreset = (presetValue) => {
    setFormat("simple");
    setDuration(presetValue);
  };

  const formatDetailedDuration = () => {
    const { hours, minutes, seconds } = detailedDuration;
    let formattedDuration = "";
    
    if (hours > 0) {
      formattedDuration += `${hours}h`;
    }
    
    if (minutes > 0 || (hours > 0 && seconds > 0)) {
      formattedDuration += `${minutes}min`;
    }
    
    if (seconds > 0) {
      formattedDuration += `${seconds}s`;
    }
    
    return formattedDuration || "0s";
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    let finalDuration;
    if (format === "detailed") {
      finalDuration = formatDetailedDuration();
    } else {
      finalDuration = duration;
    }
    
    if (!finalDuration) {
      toast.error(t("artworkModals.duration.errors.required"));
      return;
    }
    
    onSubmit(finalDuration === t("artworkModals.duration.infiniteLoop") ? "Loop infini" : finalDuration);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
         onClick={onClose}>
      <div className="bg-white p-8 rounded-md max-w-lg w-full"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-6">
          <h3 className="font-serif text-xl">{t("artworkModals.duration.title")}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-black">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {t("artworkModals.duration.description")}
            </p>
            
            {/* Choix du format d'entrée */}
            <div>
              <label className="block text-sm font-medium text-gray-dark mb-2">
                {t("artworkModals.duration.inputFormat")}
              </label>
              <div className="flex space-x-4">
                <label className="inline-flex items-center">
                  <input 
                    type="radio" 
                    checked={format === "simple"} 
                    onChange={() => setFormat("simple")} 
                    className="mr-2"
                  />
                  <span>{t("artworkModals.duration.simple")}</span>
                </label>
                <label className="inline-flex items-center">
                  <input 
                    type="radio" 
                    checked={format === "detailed"} 
                    onChange={() => setFormat("detailed")} 
                    className="mr-2"
                  />
                  <span>{t("artworkModals.duration.detailed")}</span>
                </label>
              </div>
            </div>
            
            {/* Format texte libre */}
            {format === "simple" && (
              <div>
                <label className="block text-sm font-medium text-gray-dark mb-1">
                  {t("artworkModals.duration.duration")}
                </label>
                <input
                  type="text"
                  value={duration}
                  onChange={handleDurationChange}
                  className="form-control-artsy w-full"
                  placeholder={t("artworkModals.duration.placeholder")}
                  required={format === "simple"}
                />
                <p className="text-xs text-gray-dark mt-1">
                  {t("artworkModals.duration.examples")}
                </p>
                
                {/* {t("artworkModals.duration.presets")} */}
                <div className="mt-4">
                  <label className="block text-sm mb-2">{t("artworkModals.duration.presets")}</label>
                  <div className="flex flex-wrap gap-2">
                    {presetDurations.map((preset) => (
                      <button
                        key={preset.value}
                        type="button"
                        onClick={() => applyPreset(preset.value)}
                        className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-md"
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {/* Format détaillé */}
            {format === "detailed" && (
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-dark mb-1">
                    {t("artworkModals.duration.hours")}
                  </label>
                  <input
                    type="number"
                    name="hours"
                    value={detailedDuration.hours}
                    onChange={handleDetailedChange}
                    className="form-control-artsy w-full"
                    min="0"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-dark mb-1">
                    {t("artworkModals.duration.minutes")}
                  </label>
                  <input
                    type="number"
                    name="minutes"
                    value={detailedDuration.minutes}
                    onChange={handleDetailedChange}
                    className="form-control-artsy w-full"
                    min="0"
                    max="59"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-dark mb-1">
                    {t("artworkModals.duration.seconds")}
                  </label>
                  <input
                    type="number"
                    name="seconds"
                    value={detailedDuration.seconds}
                    onChange={handleDetailedChange}
                    className="form-control-artsy w-full"
                    min="0"
                    max="59"
                  />
                </div>
              </div>
            )}
            
            <div>
              <p className="text-sm font-medium text-gray-dark mb-1">{t("artworkModals.duration.preview")}</p>
              <p className="text-sm">
                {format === "detailed" ? formatDetailedDuration() : duration || t("artworkModals.duration.none")}
              </p>
            </div>
          </div>
          
          <div className="mt-8 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="btn-artsy btn-secondary-artsy mr-2"
            >
              {t("artworkModals.common.cancel")}
            </button>
            <button
              type="submit"
              className="btn-artsy btn-primary-artsy"
              disabled={format === "simple" ? !duration : (detailedDuration.hours === 0 && detailedDuration.minutes === 0 && detailedDuration.seconds === 0)}
            >
              {t("artworkModals.common.add")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DurationModal;